import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageStatCards } from "@/components/PageStatCards";
import { TableToolbar } from "@/components/TableToolbar";
import { RowActions } from "@/components/RowActions";
import { DetailDrawer, DrawerSection, DrawerField } from "@/components/DetailDrawer";
import { EmptyState } from "@/components/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { getErrorMessage } from "@/lib/errors";

import { fmtMoney } from "@/lib/format";
/** Whole quantities without decimals ("100"), fractions kept ("2.5"). */
export function fmtQty(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 3 });
}

export default function StockBalances() {
  const [balances, setBalances] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const loadAlerts = () => api.get("/inventory/alerts").then(({ data }) => setAlerts(data)).catch((err) => toast.error(getErrorMessage(err)));

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get("/inventory/stock-balances"), api.get("/inventory/items"), api.get("/inventory/warehouses"), api.get("/inventory/alerts")])
      .then(([balRes, itemRes, whRes, alertRes]) => {
        setBalances(balRes.data); setItems(itemRes.data);
        setWarehouses(whRes.data); setAlerts(alertRes.data);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  const itemLabel = (id) => { const i = items.find((it) => it.id === id); return i ? `${i.code} — ${i.name}` : id; };
  const warehouseName = (id) => warehouses.find((w) => w.id === id)?.name || id;

  const shown = useMemo(() => {
    if (!search) return balances;
    const q = search.toLowerCase();
    return balances.filter((b) =>
      itemLabel(b.item_id).toLowerCase().includes(q) ||
      warehouseName(b.warehouse_id).toLowerCase().includes(q)
    );
  }, [balances, search, items, warehouses]);

  const totalValue = balances.reduce((s, b) => s + Number(b.value || 0), 0);
  const lowStockCount = balances.filter((b) => b.low_stock).length;

  const scanNow = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/inventory/alerts/scan");
      toast.success(`Scan complete: ${data.open_total} open, ${data.created} new, ${data.resolved} resolved`);
      loadAlerts();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setScanning(false);
    }
  };

  const acknowledge = async (alert) => {
    try {
      await api.patch(`/inventory/alerts/${alert.id}`, { status: "RESOLVED" });
      loadAlerts();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="stock-balances-page">
      <PageHeader
        title="Stock & Balances"
        description="Current on-hand quantity and weighted-average cost per item, per warehouse."
        action={
          <Button size="sm" variant="outline" disabled={scanning} onClick={scanNow} data-testid="scan-low-stock-button">
            {scanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Scan for low stock
          </Button>
        }
      />
      <PageStatCards loading={loading} stats={[
        { label: "Items Tracked", value: balances.length, color: "blue" },
        { label: "Low Stock Alerts", value: lowStockCount, color: "orange" },
        { label: "Open Alerts", value: alerts.length, color: "red" },
        { label: "Total Value (₹)", value: `₹${Number(totalValue).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`, color: "green" },
      ]} />

      {alerts.length > 0 && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/5" data-testid="low-stock-alerts-panel">
          <div className="flex items-center gap-2 border-b border-rose-500/20 px-4 py-3">
            <AlertTriangle className="h-4 w-4 text-rose-400" />
            <h2 className="text-sm font-semibold text-foreground">Low stock alerts ({alerts.length})</h2>
          </div>
          <div className="divide-y divide-border">
            {alerts.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`low-stock-alert-row-${a.id}`}>
                <div>
                  <p className="text-sm text-foreground">{a.title}</p>
                  <p className="text-xs text-muted-foreground">{a.detail} {a.project_id ? "· Tied to project" : "· Org-wide"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={a.severity === "CRITICAL" || a.severity === "HIGH" ? "destructive" : "secondary"}>{a.severity}</Badge>
                  <Button size="sm" variant="ghost" onClick={() => acknowledge(a)} data-testid={`resolve-alert-button-${a.id}`}><Check className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <TableToolbar
        search={search}
        onSearch={setSearch}
        csvData={shown.map((b) => ({
          Item: itemLabel(b.item_id),
          Warehouse: warehouseName(b.warehouse_id),
          Qty: fmtQty(b.qty),
          Unit: b.unit || "",
          "Avg Cost": b.avg_cost,
          Value: b.value,
          "Low Stock": b.low_stock ? "Yes" : "No",
        }))}
        csvFilename="stock-balances.csv"
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && balances.length === 0 ? (
          <EmptyState title="No stock movements yet" description="Receive against a GRN or a manual receipt to see balances here." testId="stock-balances-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Warehouse</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Avg cost</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead></TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((b) => (
                <TableRow key={b.id} data-testid={`stock-balance-row-${b.item_id}-${b.warehouse_id}`}>
                  <TableCell className="text-foreground">{itemLabel(b.item_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{warehouseName(b.warehouse_id)}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground" data-testid={`stock-qty-${b.item_id}`}>{fmtQty(b.qty)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground" data-testid={`stock-unit-${b.item_id}`}>{b.unit || "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">{fmtMoney(b.avg_cost)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">{b.value}</TableCell>
                  <TableCell>
                    {b.low_stock && <Badge variant="destructive" data-testid={`low-stock-badge-${b.item_id}`}><AlertTriangle className="mr-1 h-3 w-3" /> Low stock</Badge>}
                  </TableCell>
                  <TableCell>
                    <RowActions testId={`balance-actions-${b.item_id}`} onView={() => setSelected(b)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <DetailDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected ? itemLabel(selected.item_id) : ""}
        subtitle={selected ? `In ${warehouseName(selected.warehouse_id)}` : ""}
      >
        <DrawerSection label="Stock Details">
          <DrawerField label="Item" value={selected ? itemLabel(selected.item_id) : null} />
          <DrawerField label="Warehouse" value={selected ? warehouseName(selected.warehouse_id) : null} />
          <DrawerField label="Quantity on Hand" value={selected ? `${fmtQty(selected.qty)}${selected.unit ? ` ${selected.unit}` : ""}` : null} mono />
          <DrawerField label="Avg Unit Cost" value={selected?.avg_cost} mono />
          <DrawerField label="Total Value" value={selected?.value} mono />
          <DrawerField label="Low Stock Alert" value={selected?.low_stock ? "Yes" : "No"} />
        </DrawerSection>
      </DetailDrawer>
    </div>
  );
}
